import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Handle, Position } from '@xyflow/react'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { getVariantStyles, getSizeStyles } from './variants'

const HANDLE_STYLE = { width: 5, height: 5, opacity: 0 }

const CIRCLE_DIMENSIONS = {
  lg: 140,
  md: 100,
  sm: 72
}

function FlowGraphCircle ({ data, selected }) {
  const {
    nameValue, nameConfig,
    descriptionValue, descriptionConfig,
    variant = 'primary',
    size = 'md',
    color
  } = data

  const { formatter } = useFormattingContext()
  const variantStyles = getVariantStyles(variant, color)
  const sizeStyles = getSizeStyles(size)
  const dimension = CIRCLE_DIMENSIONS[size] || CIRCLE_DIMENSIONS.md

  const formattedName = nameConfig?.format
    ? formatter(nameValue, nameConfig.format)
    : nameValue

  const formattedDesc = descriptionConfig?.format && descriptionValue != null
    ? formatter(descriptionValue, descriptionConfig.format)
    : descriptionValue

  const containerStyle = useMemo(() => ({
    width: dimension,
    height: dimension,
    borderRadius: '50%',
    background: variantStyles.background,
    border: `2px solid ${selected ? variantStyles.selectedBorderColor : variantStyles.borderColor}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    boxShadow: selected ? '0 0 0 3px rgba(0,0,0,0.08)' : '0 2px 6px rgba(0,0,0,0.12)',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    padding: 8,
    overflow: 'hidden'
  }), [dimension, variantStyles, selected])

  const nameStyle = useMemo(() => ({
    color: variantStyles.color,
    fontWeight: 600,
    fontSize: sizeStyles.fontSize,
    lineHeight: 1.2,
    textAlign: 'center',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    maxWidth: dimension - 24,
    ...(nameConfig?.style || {})
  }), [variantStyles, sizeStyles, dimension, nameConfig])

  const descStyle = useMemo(() => ({
    color: variantStyles.color,
    opacity: 0.8,
    fontSize: '0.6875em',
    textAlign: 'center',
    marginTop: 2,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    maxWidth: dimension - 24,
    ...(descriptionConfig?.style || {})
  }), [variantStyles, dimension, descriptionConfig])

  return (
    <div style={containerStyle}>
      <Handle type='target' position={Position.Top} style={{ ...HANDLE_STYLE, background: variantStyles.borderColor }} />
      {formattedName != null && (
        <div className={nameConfig?.className || ''} style={nameStyle}>
          {formattedName}
        </div>
      )}
      {formattedDesc != null && (
        <div className={descriptionConfig?.className || ''} style={descStyle}>
          {formattedDesc}
        </div>
      )}
      <Handle type='source' position={Position.Bottom} style={{ ...HANDLE_STYLE, background: variantStyles.borderColor }} />
    </div>
  )
}

FlowGraphCircle.propTypes = {
  data: PropTypes.shape({
    nameValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nameConfig: PropTypes.object,
    descriptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descriptionConfig: PropTypes.object,
    variant: PropTypes.string,
    color: PropTypes.object,
    size: PropTypes.string
  }),
  selected: PropTypes.bool
}

export default memo(FlowGraphCircle)
